import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '@annote/core/styles';

export const PanelHeader = styled.div<{ $side?: 'left' | 'right' }>`
  position: absolute;
  top: 1.5rem;
  ${({ $side }) => ($side === 'left' ? 'left: 1.5rem;' : 'right: 1.5rem;')}
  z-index: 200;
  opacity: 1;
  transition: opacity 250ms ease-in-out;
`;

interface PanelProps {
  isOpen: boolean;
  side: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  offset?: number;
  resizable?: boolean;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
}

const PanelContainer = styled.div<{ $isOpen: boolean; $side: 'left' | 'right'; $offset: number; $width?: number }>`
  position: fixed;
  top: 0;
  ${({ $side, $offset }) => ($side === 'left' ? `left: ${$offset}rem;` : `right: ${$offset}rem;`)}
  ${({ $width }) => $width && `width: ${$width}px;`}
  display: flex;
  flex-direction: column;
  background: ${colors.bg.primary};
  box-shadow: ${colors.shadow.toolbar};
  height: 100vh;
  transition: transform 250ms ease-in-out, opacity 250ms ease-in-out, left 250ms ease-in-out, right 250ms ease-in-out;
  z-index: 100;

  ${({ $isOpen, $side }) => {
    if (!$isOpen) {
      return css`
        transform: translateX(${$side === 'left' ? '-100%' : '100%'});
        opacity: 0;
        pointer-events: none;
      `;
    }
    return css`
      transform: translateX(0);
      opacity: 1;
    `;
  }}
`;

const ResizeHandle = styled.div<{ $side: 'left' | 'right'; $isDragging: boolean }>`
  position: absolute;
  top: 0;
  ${({ $side }) => ($side === 'left' ? 'right: 0;' : 'left: 0;')}
  width: 4px;
  height: 100%;
  cursor: ew-resize;
  z-index: 10;
  background: ${({ $isDragging }) => ($isDragging ? colors.text.link : 'transparent')};
  transition: background 150ms ease;

  &:hover {
    background: ${colors.text.link};
  }

  &::after {
    content: '';
    position: absolute;
    ${({ $side }) => ($side === 'left' ? 'right: -4px;' : 'left: -4px;')}
    width: 8px;
    height: 100%;
  }
`;

export const Panel: React.FC<PanelProps> = ({
  isOpen,
  side,
  children,
  className,
  offset = 0,
  resizable = false,
  defaultWidth,
  minWidth = 200,
  maxWidth = 1200,
  onWidthChange,
}) => {
  const [width, setWidth] = useState<number | undefined>(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!resizable) return;
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width || 0;
    },
    [resizable, width],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const delta = side === 'left' ? e.clientX - startXRef.current : startXRef.current - e.clientX;
      let newWidth = startWidthRef.current + delta;

      // Clamp between min and max
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [isDragging, side, minWidth, maxWidth, onWidthChange],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return undefined;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Report initial width on mount
  useEffect(() => {
    if (width && onWidthChange) {
      onWidthChange(width);
    }
  }, []);

  return (
    <PanelContainer $isOpen={isOpen} $side={side} $offset={offset} $width={width} className={className}>
      {children}
      {resizable && <ResizeHandle $side={side} $isDragging={isDragging} onMouseDown={handleMouseDown} />}
    </PanelContainer>
  );
};
